import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaDownload, FaFilePdf } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { CheckCircle2, FileText, X, Mail } from 'lucide-react';

const PdfJobContext = createContext();

export const usePdfJob = () => useContext(PdfJobContext);

const EmailLoadingToast = ({ title }) => {
    return (
        <div className="flex flex-col w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {/* Top accent bar */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #dc2626, #b91c1c)',
                    borderRadius: '4px 4px 0 0',
                }}
            />

            <div className="flex items-center gap-3 pt-2">
                {/* Spinner */}
                <div
                    className="w-9 h-9 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"
                    style={{ flexShrink: 0 }}
                />

                <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>
                        Sending Email Report...
                    </p>
                    <p
                        style={{
                            fontSize: 11,
                            color: '#6b7280',
                            margin: '2px 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 200,
                        }}
                    >
                        {title}
                    </p>
                </div>
            </div>
        </div>
    );
};

const EmailSuccessToast = ({ title, closeToast }) => {
    return (
        <div className="flex flex-col w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {/* Top accent bar */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #16a34a, #4ade80)',
                    borderRadius: '4px 4px 0 0',
                }}
            />

            {/* Icon + Text row */}
            <div className="flex items-center gap-3 pt-1">
                <div
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CheckCircle2 style={{ width: 18, height: 18, color: '#16a34a' }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.3 }}>
                        Email Sent
                    </p>
                    <p
                        style={{
                            fontSize: 11,
                            color: '#6b7280',
                            margin: '2px 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 200,
                        }}
                    >
                        {title}
                    </p>
                </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#f3f4f6', margin: '10px 0 8px' }} />

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        closeToast();
                    }}
                    style={{
                        padding: '5px 12px',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#6b7280',
                        background: 'transparent',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.background = '#f9fafb';
                        e.currentTarget.style.color = '#374151';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                    }}
                >
                    Dismiss
                </button>
            </div>
        </div>
    );
};

const PdfSuccessToast = ({ closeToast, title, onView }) => {
    return (
        <div className="flex flex-col w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {/* Top accent bar */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #16a34a, #4ade80)',
                    borderRadius: '4px 4px 0 0',
                }}
            />

            {/* Icon + Text row */}
            <div className="flex items-center gap-3 pt-1">
                <div
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CheckCircle2 style={{ width: 18, height: 18, color: '#16a34a' }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.3 }}>
                        Report Ready
                    </p>
                    <p
                        style={{
                            fontSize: 11,
                            color: '#6b7280',
                            margin: '2px 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 200,
                        }}
                    >
                        {title}
                    </p>
                </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#f3f4f6', margin: '10px 0 8px' }} />

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        closeToast();
                    }}
                    style={{
                        padding: '5px 12px',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#6b7280',
                        background: 'transparent',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.background = '#f9fafb';
                        e.currentTarget.style.color = '#374151';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                    }}
                >
                    Dismiss
                </button>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onView();
                        closeToast();
                    }}
                    style={{
                        padding: '5px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#fff',
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        boxShadow: '0 1px 4px rgba(220,38,38,0.35)',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c, #991b1b)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(220,38,38,0.45)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626, #b91c1c)';
                        e.currentTarget.style.boxShadow = '0 1px 4px rgba(220,38,38,0.35)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <FileText style={{ width: 13, height: 13 }} />
                    View PDF
                </button>
            </div>
        </div>
    );
};



export const PdfJobProvider = ({ children }) => {
    const [jobs, setJobs] = useState({}); // { [id]: { status, title, url, error } }
    const [viewerJobId, setViewerJobId] = useState(null);

    const startJob = useCallback(async (title, triggerPromiseOrFn, payload) => {
        const jobId = Date.now().toString();
        setJobs(prev => ({ ...prev, [jobId]: { status: 'pending', title } }));

        // Notify start
        // const toastId = toast.loading(`Generating PDF: ${title}...`, { autoClose: false });
        const toastId = toast(
            <PdfLoadingToast title={title} />,
            {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                type: "default"
            }
        );


        try {
            // Handle both direct promise or function returning promise
            // If it's a function (like RTK trigger), we execute it with payload
            // If it's a promise, we await it
            const request = typeof triggerPromiseOrFn === 'function'
                ? triggerPromiseOrFn(payload)
                : triggerPromiseOrFn;

            if (!request) {
                throw new Error("No request initiated");
            }

            // If it's an RTK Query object with .unwrap(), use it to get raw data/error.
            // Otherwise, await the promise directly. We avoid Promise.resolve(request)
            // to prevent "Illegal invocation" errors with RTK Query's custom thenables.
            let response;
            if (typeof request.unwrap === 'function') {
                response = await request.unwrap();
            } else {
                response = await request;
            }

            if (!response) {
                throw new Error("No response received from server");
            }

            // Debug logging for Premium Calendar preview responses
            if (title && String(title).toLowerCase().includes('premium calendar')) {
                // Log full response and the normalized data object
                console.log('[PremiumCalendarReport][preview response]', response);
                console.log('[PremiumCalendarReport][preview data]', response?.data || response);
            }

            // Logic to extract PDF
            let url;
            const data = response.data || response;

            // 1. Check if response is already a Blob (native fetch returning blob)
            if (response instanceof Blob) {
                url = URL.createObjectURL(response);
            }
            // 2. Check if response has blobUrl (custom helper)
            else if (response?.blobUrl) {
                url = response.blobUrl;
            }
            // 3. Check for Base64 (RTK Query standard)
            else {
                const base64 = data?.pdf || data?.pdfBase64 || data?.data?.pdf;

                if (base64) {
                    const byteCharacters = atob(base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                    url = URL.createObjectURL(blob);
                }
            }

            if (url) {
                setJobs(prev => ({ ...prev, [jobId]: { status: 'completed', title, url } }));

                // Notify success with custom persistent component
                toast.update(toastId, {
                    render: <PdfSuccessToast title={title} onView={() => setViewerJobId(jobId)} />,
                    type: "success",
                    isLoading: false,
                    autoClose: false, // Keep open until clicked or closed manually
                    closeButton: false // We have our own dismiss button
                });
            } else {
                throw new Error(data?.message || "No PDF data received");
            }

        } catch (error) {
            console.error("PDF Job failed", error);
            const errorMsg = error?.data?.message || error?.message || "Unknown error";
            setJobs(prev => ({ ...prev, [jobId]: { status: 'failed', title, error: errorMsg } }));

            toast.update(toastId, {
                render: `Failed to generate ${title}: ${errorMsg}`,
                type: "error",
                isLoading: false,
                autoClose: 5000
            });
        }
    }, []);

    const startEmailJob = useCallback(async (title, triggerPromiseOrFn, payload) => {
        const toastId = toast(
            <EmailLoadingToast title={title} />,
            {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                type: "default"
            }
        );

        try {
            const request = typeof triggerPromiseOrFn === 'function'
                ? triggerPromiseOrFn(payload)
                : triggerPromiseOrFn;

            if (!request) {
                throw new Error("No request initiated");
            }

            let response;
            if (typeof request.unwrap === 'function') {
                response = await request.unwrap();
            } else {
                response = await request;
            }

            if (response && (response.success || response.status === 'success')) {
                toast.update(toastId, {
                    render: <EmailSuccessToast title={title} closeToast={() => toast.dismiss(toastId)} />,
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    closeButton: false
                });
            } else {
                throw new Error(response?.message || "Failed to send email");
            }
        } catch (error) {
            console.error("Email Job failed", error);
            const errorMsg = error?.data?.message || error?.message || "Unknown error";
            toast.update(toastId, {
                render: `Failed to send ${title} email: ${errorMsg}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                closeButton: true
            });
        }
    }, []);

    const closeViewer = () => {
        // Optional: Revoke object URL if we want to clean up, but we might want to keep it in history
        // For now, keep it valid so user can open it again from history if we build a history UI
        setViewerJobId(null);
    };

    const currentJob = viewerJobId ? jobs[viewerJobId] : null;

    return (
        <PdfJobContext.Provider value={{ startJob, startEmailJob, jobs }}>
            {children}
            {/* Global PDF Viewer Modal */}
            {currentJob && currentJob.url && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-[96vw] h-[95vh] flex flex-col relative animate-in fade-in zoom-in duration-200">

                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b bg-gray-50 rounded-t-lg">
                            <div className="flex items-center gap-3">
                                <div className="bg-red-100 p-2 rounded-full">
                                    <FaFilePdf className="text-red-600 text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-lg">{currentJob.title}</h3>
                                    <p className="text-xs text-gray-500">Preview Mode</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Download button */}
                                <button
                                    onClick={() => {
                                        const a = document.createElement('a');
                                        a.href = currentJob.url;
                                        a.download = `${currentJob.title.replace(/[^a-z0-9]/gi, '_')}.pdf`; // sanitize filename
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                    }}
                                    className="saveButton d-flex align-items-center px-4"
                                    aria-label="Download PDF"
                                    title="Download PDF"
                                >
                                    {/* <FaDownload size={20} className="text-gray-600" /> */}
                                    Download
                                </button>
                                {/* Close button */}
                                <button
                                    onClick={closeViewer}
                                    className="backButton d-flex align-items-center px-4"
                                    aria-label="Close"
                                >
                                    {/* <IoMdClose size={24} className="text-gray-600" /> */}
                                    Close
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 bg-gray-100 p-0 overflow-hidden relative">
                            <iframe
                                src={currentJob.url}
                                className="w-full h-full border-0"
                                title="PDF Preview"
                            />
                        </div>
                    </div>
                </div>
            )}
        </PdfJobContext.Provider>
    );
};



const PdfLoadingToast = ({ title }) => {
    return (
        <div className="flex flex-col w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>

            {/* Top accent bar */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #dc2626, #b91c1c)',
                    borderRadius: '4px 4px 0 0',
                }}
            />

            <div className="flex items-center gap-3 pt-2">

                {/* Spinner */}
                <div
                    className="w-9 h-9 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"
                    style={{ flexShrink: 0 }}
                />

                <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>
                        Generating PDF...
                    </p>
                    <p
                        style={{
                            fontSize: 11,
                            color: '#6b7280',
                            margin: '2px 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 200,
                        }}
                    >
                        {title}
                    </p>
                </div>
            </div>
        </div>
    );
};
